import { useEffect, useRef, type ReactNode } from 'react';

export interface ChatSessionSummary {
  id: string;
  title: string;
  mode: string;
  created_at_ms: number;
  updated_at_ms: number;
}

/** Scope filter for the chat list. 'all' = every session this user has;
 *  a product slug filters by `ChatSession.metadata.product`. Lets the user
 *  see only the chats that started inside a particular product widget. */
export type ChatScope = 'all' | 'esg' | 'lumara' | 'datacaffe' | 'electioncaffe';

interface Props {
  sessions: ChatSessionSummary[];
  activeId: string | null;
  loading: boolean;
  /** True while more pages exist on the server (drives the scroll sentinel). */
  hasMore?: boolean;
  /** Fetch the next server page — called when the bottom sentinel scrolls in. */
  onLoadMore?(): void;
  /** True while a next-page fetch is in flight (renders a small spinner row). */
  loadingMore?: boolean;
  /** Optional — the list auto-refreshes now; kept for back-compat. */
  onRefresh?(): void;
  /** Optional — New chat now lives in the SidebarNav; kept for back-compat. */
  onNewChat?(): void;
  onSelect(id: string): void;
  onDelete(id: string): void;
  /** Optional control rendered above the chat list (e.g. surface switcher). */
  topSlot?: ReactNode;
  /** Optional control pinned to the bottom of the sidebar (e.g. profile pill). */
  bottomSlot?: ReactNode;
}

/**
 * Sidebar Chats panel.
 *
 * DOM matches the legacy `<aside class="sidebar"> > <section class="sb-panel">`
 * shape so all the legacy CSS (sb-panel__header, sb-panel__cta,
 * sidebar__chats, sb-panel__footer) styles it identically.
 *
 * Pagination is SERVER-driven (keyset cursor): the parent holds only the pages
 * it has fetched, and the bottom sentinel asks for the next page on scroll. The
 * first paint stays cheap no matter how long the user's history is.
 */
export function ChatSidebarPanel({
  sessions, activeId, loading,
  hasMore = false, onLoadMore, loadingMore = false,
  onSelect, onDelete,
  topSlot,
}: Props) {
  // Flat, newest-first list. The server already returns pages newest-first, but
  // we re-sort defensively so a locally-bumped session (new message) surfaces.
  const sortedSessions = [...sessions].sort((a, b) => b.updated_at_ms - a.updated_at_ms);

  const sentinelRef = useRef<HTMLLIElement>(null);
  const visible = sortedSessions;

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { rootMargin: '160px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, onLoadMore, sessions.length]);

  return (
      <section className="sb-panel" data-panel="chats">
        <header className="sb-panel__header">
          <h2 className="sb-panel__title">Recent</h2>
        </header>

        {topSlot && <div className="sb-switch">{topSlot}</div>}

        <ul className="sidebar__chats" id="sidebarChats">
          {loading && (
            <>
              <li className="chat-skel"><span /><span /></li>
              <li className="chat-skel"><span /><span /></li>
              <li className="chat-skel"><span /><span /></li>
            </>
          )}
          {!loading && sessions.length === 0 && (
            <li className="chats-empty">No chats yet. Hit “New chat” to start.</li>
          )}
          {!loading && visible.map((s) => {
            const isActive = s.id === activeId;
            return (
              <li
                key={s.id}
                className={`sidebar__chat${isActive ? ' is-active' : ''}`}
                onClick={() => onSelect(s.id)}
                title={s.title}
              >
                <span className="sidebar__chat-title">{s.title}</span>
                <button
                  type="button"
                  className="sidebar__chat-del"
                  aria-label="Delete chat"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete “${s.title}”?`)) onDelete(s.id);
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
          {!loading && loadingMore && (
            <li className="chat-skel"><span /><span /></li>
          )}
          {!loading && hasMore && (
            <li ref={sentinelRef} className="chats-sentinel" aria-hidden="true" />
          )}
        </ul>
      </section>
  );
}
